import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { api, type Channel } from "../lib/api.js";
import { useAuth } from "../lib/auth.js";
import { isAttending } from "../lib/attention.js";
import { useRealtimeFrames } from "../lib/realtime-context.js";

/**
 * The Chat nav item's badge: a total across every channel and DM, the same
 * number `Home.tsx`'s "Unread messages" tile already shows — just visible
 * from any screen instead of only the landing page.
 *
 * Reloads on every route change so leaving Chat (which marks channels read)
 * clears the badge without a full page refresh, and bumps live on incoming
 * messages the same way Chat's own sidebar does. Own messages are excluded —
 * the server fans a "message" frame out to everyone in the channel including
 * its sender, and a message you just sent is not unread to you.
 */
export function useUnreadChatTotal(): number {
  const { viewer } = useAuth();
  const location = useLocation();
  const [total, setTotal] = useState(0);
  /*
   * The active office's channels — the ones the total counts. The socket also
   * delivers messages for the viewer's other offices (so they notify), and
   * bumping the badge for those would leave it disagreeing with the next load.
   */
  const channelIds = useRef<Set<string>>(new Set());

  /*
   * A count fetched while a message arrives cannot be trusted: the server may
   * have read the counts before that message was stored, and applying the
   * result would overwrite the +1 the frame just added. (Before the first
   * fetch returns, the frame is not even counted: no channel is known yet.)
   * So a message landing mid-fetch marks it stale, and a stale result is
   * dropped for a fresh fetch, which the server, having already sent the
   * frame, is sure to include. Only the latest fetch is ever applied.
   */
  const fetchSeq = useRef(0);
  const inFlight = useRef(false);
  const stale = useRef(false);

  const load = useCallback(() => {
    if (!viewer) return;
    const fetchCounts = () => {
      const seq = ++fetchSeq.current;
      inFlight.current = true;
      stale.current = false;
      api
        .get<{ channels: Channel[] }>("/channels")
        .then((res) => {
          if (seq !== fetchSeq.current) return;
          inFlight.current = false;
          if (stale.current) {
            fetchCounts();
            return;
          }
          channelIds.current = new Set(res.channels.map((c) => c.id));
          setTotal(res.channels.reduce((sum, c) => sum + (c.unreadCount ?? 0), 0));
        })
        .catch(() => {
          if (seq === fetchSeq.current) inFlight.current = false;
        });
    };
    fetchCounts();
  }, [viewer]);

  useEffect(() => load(), [load, location.pathname]);

  const reloadTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(reloadTimer.current), []);

  /*
   * The channel on screen, if any. A message arriving in it is read as it
   * lands — Chat marks the conversation read — so counting it inflates a
   * badge that nothing here ever decrements.
   */
  const openChannelId = location.pathname.match(/^\/chat\/([^/]+)/)?.[1] ?? null;

  useRealtimeFrames((frame) => {
    /*
     * The socket is subscribed — on the first connection as well as after a
     * reconnect. The mount fetch can land before that, and a message posted
     * in the gap reaches neither it nor the socket, so count again now.
     */
    if (frame.t === "ready") {
      load();
      return;
    }
    /*
     * The viewer read a channel — here or on another device. The route-change
     * fetch above races the read it triggers and could land first, leaving
     * the channel just opened counted until the next navigation; the server
     * sends this frame after the read is stored, so refetching now is exact.
     */
    if (frame.t === "read" && frame.userId === viewer?.id) {
      // Debounced: an active conversation sends one read per incoming message.
      clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(load, 400);
      return;
    }
    // A channel just became readable: learn it, so its messages count.
    if (frame.t === "channel_new") {
      load();
      return;
    }
    if (frame.t !== "message" || frame.message.authorId === viewer?.id) return;
    if (inFlight.current) stale.current = true;
    if (!channelIds.current.has(frame.message.channelId)) return;
    // Only skipped when somebody is looking: Chat holds the read back for a
    // hidden window, so the message really is unread until they return.
    if (frame.message.channelId === openChannelId && isAttending()) return;
    setTotal((prev) => prev + 1);
  });

  return total;
}
